import classNames from 'classnames'
import { motion, type HTMLMotionProps, type Variants } from 'framer-motion'
import React from 'react'

import { Body1, Heading4 } from '@ui/Typography'

import styles from './ContentBlock.module.scss'

type ContentBlockVariant = 'voucher' | 'list'

interface ContentBlockBaseProps extends Omit<HTMLMotionProps<'div'>, 'children'> {
  variant: ContentBlockVariant
  animated?: boolean
  children?: React.ReactNode
}

export interface ContentBlockVoucherProps extends ContentBlockBaseProps {
  variant: 'voucher'
  icon?: React.ReactNode
  title: string
  description?: (string | React.ReactNode) | (string | React.ReactNode)[]
}

export interface ContentBlockRow {
  label: string
  value: React.ReactNode
  strongValue?: boolean
  leftAddon?: React.ReactNode
  rightAddon?: React.ReactNode
}

export interface ContentBlockListProps extends ContentBlockBaseProps {
  variant: 'list'
  leftAddon?: React.ReactNode
  rightAddon?: React.ReactNode
  rows: ContentBlockRow[]
}

export type ContentBlockProps = ContentBlockVoucherProps | ContentBlockListProps

const contentBlockVariants: Variants = {
  hidden: { opacity: 0, scale: 0.95, y: 10 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: { type: 'spring', stiffness: 260, damping: 20 },
  },
}

const iconVariants: Variants = {
  hidden: { scale: 0, rotate: -45 },
  visible: {
    scale: 1,
    rotate: 0,
    transition: { type: 'spring', stiffness: 260, damping: 15, delay: 0.1 },
  },
}

export const ContentBlock: React.FC<ContentBlockProps> = (props) => {
  if (props.variant === 'voucher') {
    const {
      icon,
      title,
      description,
      children,
      animated = true,
      initial = animated ? 'hidden' : undefined,
      animate = animated ? 'visible' : undefined,
      ...rest
    } = props

    const parsedDescription = Array.isArray(description)
      ? description
      : description
      ? [description]
      : []

    return (
      <motion.div
        variants={contentBlockVariants}
        initial={initial}
        animate={animate}
        className={classNames(styles.contentBlock, styles.voucher)}
        {...rest}
      >
        {icon && (
          <motion.div variants={iconVariants} className={styles.icon}>
            {icon}
          </motion.div>
        )}
        <Heading4 medium className={styles.title}>
          {title}
        </Heading4>
        {description && (
          <div className={styles.description}>
            {parsedDescription.map((item) => (
              <Body1 key={item?.toString()}>{item}</Body1>
            ))}
          </div>
        )}
        {children}
      </motion.div>
    )
  }

  const {
    rows,
    children,
    leftAddon,
    rightAddon,
    animated = true,
    initial = animated ? 'hidden' : undefined,
    animate = animated ? 'visible' : undefined,
    ...rest
  } = props

  const renderAddon = (addon: React.ReactNode) =>
    typeof addon === 'string' ? <img src={addon} alt='' width={24} height={24} /> : addon

  return (
    <motion.div
      variants={contentBlockVariants}
      initial={initial}
      animate={animate}
      className={classNames(styles.contentBlock, styles.list)}
      {...rest}
    >
      <div className={styles.rows}>
        {rows.map((row, idx) => (
          <div className={styles.row} key={`${row.label}-${idx}`}>
            <Body1 className={styles.label}>{row.label}</Body1>
            <Body1
              as='div'
              className={classNames(styles.value, {
                [styles.strongValue]: row.strongValue,
              })}
            >
              {(row.leftAddon ?? leftAddon) && (
                <div className={styles.leftAddon}>{renderAddon(row.leftAddon ?? leftAddon)}</div>
              )}
              {row.value}
              {(row.rightAddon ?? rightAddon) && (
                <div className={styles.rightAddon}>{renderAddon(row.rightAddon ?? rightAddon)}</div>
              )}
            </Body1>
          </div>
        ))}
      </div>
      {children}
    </motion.div>
  )
}
